import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { difference } from 'lodash'

import BaseModal from 'components/Modals/BaseModal'
import { Button } from 'components/Forms'
import { closeModal } from 'store/actions/modals'
import { Trans } from 'react-i18next'
import { Loading } from 'components/UI'
import { Card, List } from 'ui'
import {
  getAttributeName,
  getDepartmentName,
  getHiringDataName,
  getPermissionName,
  getPositionName,
  getProjectName,
  getTagName,
  getTeamName,
  getUserName,
  hasAccessToThis
} from 'services/Utilities'
import { Format, Parse } from 'services/Dates'
import useApi from 'components/Hooks/useApi'
import { t } from 'translations'

const CHAT_GROUP_ID = 'acce3310c4a1acce3310c4a1'
const languageHash = { fr: 'manager.users.FRENCH', en: 'manager.users.ENGLISH' }

const UserDiffModal = ({ closeModal, userId, timeZone }) => {
  const { isLoading: loading, response: users, handleApi: fetchUser } = useApi({ url: '/users/users', autoExecute: false })

  useEffect(() => {
    fetchUser({ params: { _id: userId, returnDiffs: true } })
  }, [userId, fetchUser])

  const informationAreUnavailable = property => (
    <i>
      {t('manager.users.diffs.INFORMATION_UNAVAILABLE')} {hasAccessToThis('isMeringoAdmin') ? ` Property: ${property}.` : null}
    </i>
  )

  const getElementName = (element, property) => {
    if (CHAT_GROUP_ID === element) return null
    if (property === 'groups') return getPermissionName(element)
    else if (property === 'departments') return getDepartmentName(element)
    else if (property === 'projects') return getProjectName(element)
    else if (property === 'regionalTags') return getTagName(element)
    else if (property === 'teams') return getTeamName(element)
    else if (property === 'positions') return getPositionName(element)
    else if (property === 'hiringData') return getHiringDataName(element.hiringDataId)
    else if (property === 'specificRestrictions') return element
    else if (property === 'attributes') return getAttributeName(element.attributeId)
    return 'N/A'
  }

  const renderValue = ({ after, before, property }) => {
    const noValue = value => !value || value === ''
    const formatDate = date => {
      if (noValue(date)) return date
      try {
        return Format(Parse(date), { outFormat: 'date' })
      } catch (error) {
        return date
      }
    }

    switch (property) {
      // Line to ignore
      case 'fullName':
      case 'preferredPronounOther':
        return null
      // Direct value can be used
      case 'firstName':
      case 'lastName':
      case 'employeeNumber':
      case 'employeeID':
      case 'priorityNumber':
      case 'username':
      case 'vacationMinutesBanked':
        return (
          <Trans t={t} i18nKey="manager.users.diffs.SIMPLE_FIELD">
            {{ property: t(`manager.users.${property.toUpperCase()}`) }}
            <b>{{ before }}</b>
            <b>{{ after }}</b>
          </Trans>
        )

      // Needs date parsing
      case 'startDate':
      case 'terminationDate':
      case 'birthDate':
      case 'hiringDate':
      case 'seniorityDate': {
        let transKey = 'manager.users.diffs.SIMPLE_FIELD'
        if (noValue(before)) transKey = 'manager.users.diffs.SIMPLE_FIELD_ADDED'
        else if (noValue(after)) transKey = 'manager.users.diffs.SIMPLE_FIELD_REMOVED'
        before = formatDate(before)
        after = formatDate(after)

        return (
          <Trans t={t} i18nKey={transKey}>
            {{ property: t(`manager.users.${property.toUpperCase()}`) }}
            <b>{{ before }}</b>
            <b>{{ after }}</b>
          </Trans>
        )
      }

      // Needs translations
      case 'archived':
      case 'language':
        return (
          <Trans t={t} i18nKey="manager.users.diffs.SIMPLE_FIELD">
            {{ property: t(`manager.users.${property.toUpperCase()}`) }}
            <b>{{ before: t(languageHash[before]) }}</b>
            <b>{{ after: t(languageHash[after]) }}</b>
          </Trans>
        )

      case 'preferredPronouns':
        return (
          <Trans t={t} i18nKey="manager.users.diffs.SIMPLE_FIELD">
            {{ property: t(`manager.users.${property.toUpperCase()}`) }}
            <b>{{ before: t(`manager.users.pronouns.${before?.toUpperCase() || 'OTHER_SIMPLE'}`) }}</b>
            <b>{{ after: t(`manager.users.pronouns.${after?.toUpperCase() || 'OTHER_SIMPLE'}`) }}</b>
          </Trans>
        )

      case 'groups':
      case 'teams':
      case 'departments':
      case 'positions':
      case 'regionalTags':
      case 'specificRestrictions':
      case 'hiringData':
      case 'attributes': {
        if (!before || !after) return informationAreUnavailable(property)

        const addedElements = difference(after, before)
          .map(el => getElementName(el, property))
          .join(', ')
        const removedElements = difference(before, after)
          .map(el => getElementName(el, property))
          .join(', ')

        // Nothing to display (chat assignation, resource deleted from the app)
        if (addedElements === '' && removedElements === '') return informationAreUnavailable(property)

        const isModification = removedElements.length && addedElements.length
        const allRemoved = removedElements.length && !addedElements.length
        const addFromEmpty = !removedElements.length && addedElements.length

        return (
          <>
            <Trans
              t={t}
              i18nKey={`manager.users.diffs.ADVANCED_FIELD_${isModification ? 'MODIFICATION' : allRemoved ? 'ALL_REMOVED' : addFromEmpty ? 'ADDED' : 'TODO'}`}
            >
              {{ property: t(`manager.users.${property.toUpperCase()}`) }}
              <b>{{ addedElements }}</b>
              <b>{{ removedElements }}</b>
            </Trans>
          </>
        )
      }

      case 'address':
      case 'clientSeniorities':
      case 'positionSeniorities':
      case 'emergencyContacts':
      case 'emails':
      case 'phoneNumbers':
      case 'projects':
        return t(`manager.users.diffs.${property.toUpperCase()}_MODIFIED`)
      case 'projectRestrictions':
        return t(`manager.users.diffs.${property.toUpperCase()}_MODIFIED`)

      case 'occupations':
        if (!before && !after && !Object.keys(before).length && !Object.keys(after).length) return informationAreUnavailable(property)

        return [
          { type: 'OCCUPATION_PRIMARY', key: 'primary' },
          { type: 'OCCUPATION_SECONDARY', key: 'secondary' }
        ].map(({ type, key }, index) => {
          const isModification = before && before[key] && after && after[key]
          const allRemoved = before && before[key] && !after
          const addFromEmpty = !before && after && after[key]

          return (
            <div key={`${type}-${key}-${index}`}>
              <Trans
                t={t}
                i18nKey={`manager.users.diffs.ADVANCED_FIELD_${isModification ? 'MODIFICATION' : allRemoved ? 'ALL_REMOVED' : addFromEmpty ? 'ADDED' : 'TODO'}`}
              >
                {{ property: t(`manager.users.diffs.${type}`) }}
                <b>{{ addedElements: after && after[key] ? getPositionName(after[key]) : null }}</b>
                <b>{{ removedElements: before && before[key] ? getPositionName(before[key]) : null }}</b>
              </Trans>
            </div>
          )
        })

      case 'pay':
        if (!before && !after && !Object.keys(before).length && !Object.keys(after).length) return informationAreUnavailable(property)

        return [
          { type: 'PAY_REGULAR', key: 'regular' },
          { type: 'PAY_OVERTIME', key: 'overtime' }
        ].map(({ type, key }, index) => {
          const isModification = before && before[key] && after && after[key]
          const allRemoved = before && before[key] && !after
          const addFromEmpty = !before && after && after[key]

          return (
            <div key={`${type}-${key}-${index}`}>
              <Trans
                t={t}
                i18nKey={`manager.users.diffs.ADVANCED_FIELD_${isModification ? 'MODIFICATION' : allRemoved ? 'ALL_REMOVED' : addFromEmpty ? 'ADDED' : 'TODO'}`}
              >
                {{ property: t(`manager.users.diffs.${type}`) }}
                <b>{{ addedElements: after && after[key] ? after[key] : null }}</b>
                <b>{{ removedElements: before && before[key] ? before[key] : null }}</b>
              </Trans>
            </div>
          )
        })

      // Not done
      default:
        return hasAccessToThis('isMeringoAdmin') ? `Property (visible for staff only): ${property} is not configured.` : informationAreUnavailable(property)
    }
  }

  if (loading) return <Loading />

  const user = users[0]

  return (
    <BaseModal hideButtons title="manager.users.diffs" titleVar={{ fullName: `${user.firstName} ${user.lastName}` }}>
      <Card maxHeight="30rem">
        <List className="list-group-flush">
          {user?.diffs.map(({ lastUpdated, by, archived, values = [] }, index) => (
            <List.Item key={`${lastUpdated}-${index}`}>
              <Trans t={t} i18nKey="manager.users.diffs.TIMESTAMP">
                <b>{{ name: getUserName(by) }}</b>
                <b>{{ time: Format(lastUpdated, { outFormat: 'time', isUTC: true, timeZone }) }}</b>
                {{ date: Format(lastUpdated, { outFormat: 'humanDate', isUTC: true, timeZone }) }}
              </Trans>
              {typeof archived !== 'undefined' ? (
                <div className="text-danger font-weight-bold">{t(`manager.users.diffs.USER_${archived ? 'ARCHIVED' : 'UNARCHIVED'}`)}</div>
              ) : null}
              {values.map((value, index) => (
                <div key={index}>{renderValue(value)}</div>
              ))}
            </List.Item>
          ))}
          {user?.diffs.length ? <List.Item>{t('components.NOTHING_TO_DISPLAY')}</List.Item> : null}
        </List>
      </Card>
      <div className="mt-3">
        <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => closeModal()} />
      </div>
    </BaseModal>
  )
}

const mapStateToProps = ({ settings }) => ({
  timeZone: settings.defaultTimeZone
})
const mapDispatchToProps = dispatch => ({ closeModal: () => dispatch(closeModal()) })

export default connect(mapStateToProps, mapDispatchToProps)(UserDiffModal)
